import { authClient } from './authClient';

// The console's signed-in connection to one account's data plane. Access tokens
// live in memory only and are renewed through the auth host's session cookie;
// the only thing stored is which account was last used (not a secret), so a
// reload can resume without signing in again.

const ACCOUNT_KEY = 'signinAccountId';
const RENEW_BEFORE_MS = 60_000;

export class TokenSession {
  private token = '';
  private expiresAt = 0;
  private pending: Promise<string> | null = null;

  private constructor(
    public readonly accountId: string,
    public readonly accountName: string,
    public readonly apiBaseUrl: string,
  ) {}

  /** Starts a session for an account: fetches the first token, which also proves access. */
  static async start(accountId: string): Promise<TokenSession> {
    const issued = await authClient.token(accountId);
    const session = new TokenSession(issued.account.id, issued.account.name, issued.apiBaseUrl);
    session.accept(issued.accessToken, issued.expiresIn);
    try {
      localStorage.setItem(ACCOUNT_KEY, issued.account.id);
    } catch {
      // Storage blocked: the session still works until reload.
    }
    return session;
  }

  /** Resumes after a reload if the auth session is still alive; otherwise null. */
  static async resume(): Promise<TokenSession | null> {
    let accountId: string | null = null;
    try {
      accountId = localStorage.getItem(ACCOUNT_KEY);
    } catch {
      return null;
    }
    if (!accountId) {
      return null;
    }
    try {
      return await TokenSession.start(accountId);
    } catch {
      TokenSession.forget();
      return null;
    }
  }

  static forget(): void {
    try {
      localStorage.removeItem(ACCOUNT_KEY);
    } catch {
      // nothing to clear
    }
  }

  /** A current access token, renewed when under a minute remains (or when forced). */
  async accessToken(force = false): Promise<string> {
    if (!force && this.token && this.expiresAt - Date.now() > RENEW_BEFORE_MS) {
      return this.token;
    }
    if (!this.pending) {
      this.pending = authClient
        .token(this.accountId)
        .then((issued) => {
          this.accept(issued.accessToken, issued.expiresIn);
          return this.token;
        })
        .finally(() => {
          this.pending = null;
        });
    }
    return this.pending;
  }

  async signOut(): Promise<void> {
    TokenSession.forget();
    this.token = '';
    this.expiresAt = 0;
    await authClient.logout().catch(() => undefined);
  }

  private accept(token: string, expiresIn: number): void {
    this.token = token;
    this.expiresAt = Date.now() + expiresIn * 1000;
  }
}
